// In-memory reference implementation of `Session`.
//
// Default factory for callers that don't have a TanStack-Query-backed
// session: tests, CLI tools, standalone-mode evaluation. The chat
// plugin supplies its own implementation; this one is intentionally
// minimal — a JSON-key-hashed `Map` for the fetch cache plus a plain
// `Map<string, Value>` for bindings.

import type { Session } from './Session.ts';

/**
 * Build a fresh in-memory `Session`. Each call returns an independent
 * session — the I/O memoization cache is not shared between instances.
 *
 * The fetch cache key is `JSON.stringify(key)`; unserialisable
 * elements (Dates, Datasets, functions) produce a key that may
 * collide with other unserialisable elements. The chat plugin's
 * TanStack-backed implementation handles this more carefully via
 * `queryKeyHashFn`; the in-memory variant trades fidelity for
 * simplicity.
 *
 * Concurrent `fetch` calls with the same key coalesce on the stored
 * `Promise` — the second caller `await`s the first's in-flight
 * fetcher. This is the standard cache-miss-stampede guard.
 *
 * Cross-turn dataset bindings are not session state — they are a pure
 * projection of the message transcript resolved via
 * `createBindingResolver`, which memoizes its recursive lookups
 * through this session's `fetch`.
 */
export const createInMemorySession = (): Session => {
  const cache = new Map<string, Promise<unknown>>();
  const interned = new Map<string, unknown>();

  const hash = (key: readonly unknown[]): string => JSON.stringify(key);

  // Stable per-instance id — `Session.id` consumers (Suspense queries
  // namespaced by session) rely on uniqueness across concurrent
  // sessions in the same process.
  const id = crypto.randomUUID();

  return {
    id,
    // Non-async on purpose — returns the *cached* Promise reference
    // directly for repeat keys. Suspense consumers (`use(session.fetch(...))`
    // in `useMaterializedExpr`) require reference stability across
    // renders; an `async` wrapper would yield a fresh outer Promise
    // per call and defeat `use()`'s identity check.
    fetch: <T>(key: readonly unknown[], fetcher: () => Promise<T>): Promise<T> => {
      const h = hash(key);
      const existing = cache.get(h);
      if (existing !== undefined) {
        return existing as Promise<T>;
      }
      const promise = fetcher().catch((err) => {
        // Failed fetches don't poison the cache — remove on rejection
        // so a retry triggers a fresh fetcher invocation.
        cache.delete(h);
        throw err;
      });
      cache.set(h, promise);
      return promise;
    },
    intern: (key, value) => {
      interned.set(key, value);
    },
    resolveInterned: (key) => interned.get(key),
    invalidate: (pattern) => {
      if (pattern === undefined) {
        cache.clear();
        return;
      }
      const prefix = JSON.stringify(pattern).slice(0, -1); // drop trailing ']' so prefix matches longer arrays
      for (const k of cache.keys()) {
        if (k.startsWith(prefix)) {
          cache.delete(k);
        }
      }
    },
  };
};
